import type { MailBodyBlock } from "./mailBodyBlocks";
import {
  ensureMailContactItems,
  mailContactItemFieldKey,
  parseMailContactItemFieldKey,
} from "./mailContactBlock";
import { MAIL_GOOGLE_REVIEW_FIELD_KEY } from "./mailGoogleReviewBlock";
import { linePartsToPlainPreview } from "./mailLineParts";

export function patchBlock(
  blocks: MailBodyBlock[],
  id: string,
  next: MailBodyBlock,
): MailBodyBlock[] {
  return blocks.map((b) => (b.id === id ? next : b));
}

export function defaultFieldForBlock(block: MailBodyBlock): { field: string; value: string } {
  switch (block.type) {
    case "title":
    case "footer":
      return { field: "parts", value: linePartsToPlainPreview(block.parts) };
    case "paragraph":
      if (block.variant === "boxed") {
        return { field: "parts", value: linePartsToPlainPreview(block.parts) };
      }

      if (block.content.format === "conditional") {
        return { field: "parts", value: linePartsToPlainPreview(block.content.ifParts) };
      }

      if (block.content.format === "template") {
        return { field: "parts", value: block.content.html };
      }

      return { field: "parts", value: linePartsToPlainPreview(block.content.parts) };
    case "html":
      return { field: "html", value: block.html };
    case "contact": {
      const items = ensureMailContactItems(block.items);
      const first = items.find((item) => item.enabled) ?? items[0];

      return {
        field: first ? mailContactItemFieldKey(first.kind) : "html",
        value: first?.value ?? "",
      };
    }
    case "googleReview":
      return { field: MAIL_GOOGLE_REVIEW_FIELD_KEY, value: block.href };
    case "unsubscribe":
      return { field: "html", value: "" };
    default:
      return { field: "html", value: "" };
  }
}

export function applyTextFieldUpdate(
  blockId: string,
  field: string,
  nextVal: string,
  blocks: MailBodyBlock[],
  onChange: (b: MailBodyBlock[]) => void,
): void {
  const block = blocks.find((b) => b.id === blockId);

  if (!block) return;

  let next: MailBodyBlock;

  switch (block.type) {
    case "html":
      next = { ...block, html: nextVal };
      break;
    case "contact": {
      const kind = parseMailContactItemFieldKey(field);

      if (!kind) {
        return;
      }

      next = {
        ...block,
        items: ensureMailContactItems(block.items).map((item) =>
          item.kind === kind ? { ...item, value: nextVal, enabled: true } : item,
        ),
      };
      break;
    }
    case "googleReview":
      if (field !== MAIL_GOOGLE_REVIEW_FIELD_KEY) {
        return;
      }

      next = { ...block, href: nextVal };
      break;
    default:
      return;
  }

  onChange(patchBlock(blocks, blockId, next));
}
